import { createSelection } from './selection.js';

// The layer registry: what apparatus exists, which of it is switched on, and
// where each piece is allowed to render.
//
// A layer descriptor:
//
//   id           string    stable, used for persistence and URL state
//   label        string    what the reader sees on its tab/toggle
//   kind         string    'variants' | 'commentary' | 'parallels' | 'synopsis'
//                          | 'image' | 'overlay' -- for grouping the controls
//                          and for styling; the registry itself never branches
//                          on it
//   placement    string    'inline' (rendered into the text body by decorate)
//                          or the name of a dock ('bottom', 'side')
//   defaultOn    bool      on when no stored preference and no profile says
//   available(ctx) -> bool is this layer relevant to the loaded work at all?
//                          An unavailable layer's control is hidden, not just
//                          disabled, and its stored "on" is not honoured --
//                          otherwise state silently carries across works that
//                          don't have the data (a bug this site already hit
//                          once with the edition-numbering overlays).
//   mount(container, ctx)  dock layers only -> instance
//   decorate(bodyEl, ctx)  inline layers only; called after the body renders
//
// `kind` is deliberately inert here. The moment the registry starts special-
// casing kinds, adding the fifth commentary means editing the registry, which
// is the thing this exists to stop.

export function createLayerRegistry(options = {}) {
    const {
        docks = {},
        storageKey = null,
        profiles = {},
        selection = createSelection(),
    } = options;

    const layers = new Map();
    const changeSubscribers = new Set();
    let ctx = null;
    let activeProfile = null;
    let restored = null;      // stored preference, consulted lazily per layer

    // Whether the reader *wants* a layer, kept strictly separate from whether
    // it's currently *available*. Availability masks intent; it must not
    // overwrite it -- otherwise switching to a tractate that lacks the data
    // (Kelim has no manuscript images) and back again silently loses the
    // setting, which is what an earlier cut of this did.
    const intent = new Map();     // id -> bool

    function intentFor(layer) {
        if (intent.has(layer.id)) return intent.get(layer.id);
        // First time we've had to decide for this layer: a profile wins, then
        // the stored preference, then the layer's own default.
        const profileList = activeProfile ? profiles[activeProfile] : null;
        const source = profileList || (restored && restored.enabled) || null;
        const on = source ? source.includes(layer.id) : !!layer.defaultOn;
        intent.set(layer.id, on);
        return on;
    }

    // --- persistence -------------------------------------------------------
    function load() {
        if (!storageKey) return null;
        try {
            const raw = localStorage.getItem(storageKey);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    }

    function persist() {
        if (!storageKey) return;
        try {
            localStorage.setItem(storageKey, JSON.stringify({
                profile: activeProfile,
                // Intent, not the currently-enabled set: a layer switched on
                // but masked by the current tractate's missing data should
                // still be on when the reader opens a tractate that has it.
                enabled: [...intent.entries()].filter(([, on]) => on).map(([id]) => id),
            }));
        } catch { /* private browsing, quota -- preferences are not worth throwing over */ }
    }

    restored = load();
    if (restored && restored.profile) activeProfile = restored.profile;

    // --- availability ------------------------------------------------------
    function isAvailable(layer) {
        if (typeof layer.available !== 'function') return true;
        try {
            return !!layer.available(ctx);
        } catch (err) {
            console.error(`[mvc] layer "${layer.id}" available() threw`, err);
            return false;
        }
    }

    function availableLayers() {
        return [...layers.values()].filter(isAvailable);
    }

    function enabledLayers() {
        return availableLayers().filter(intentFor);
    }

    // --- wiring ------------------------------------------------------------
    function syncDocks() {
        Object.entries(docks).forEach(([name, dock]) => {
            const mine = enabledLayers().filter(l => l.placement === name);
            dock.setLayers(mine, ctx);
            dock.toggle(mine.length > 0);
        });
    }

    function emitChange(reason) {
        [...changeSubscribers].forEach(fn => {
            try { fn({ reason, registry: api }); } catch (err) {
                console.error('[mvc] layer-change subscriber failed', err);
            }
        });
    }

    selection.subscribe((addr, meta) => {
        Object.values(docks).forEach(dock => dock.select(addr, meta));
    });

    const api = {
        selection,

        register(layer) {
            if (!layer || !layer.id) throw new Error('registerLayer: `id` is required');
            if (layers.has(layer.id)) throw new Error(`registerLayer: duplicate id "${layer.id}"`);
            const isDock = layer.placement && layer.placement !== 'inline';
            if (isDock && typeof layer.mount !== 'function') {
                throw new Error(`registerLayer: dock layer "${layer.id}" needs mount()`);
            }
            if (!isDock && typeof layer.decorate !== 'function') {
                throw new Error(`registerLayer: inline layer "${layer.id}" needs decorate()`);
            }
            layers.set(layer.id, layer);
            return api;
        },

        // Called when a new work loads. Re-runs availability and re-seats the
        // docks. Intent is untouched: what the reader asked for doesn't change
        // because the tractate did.
        setContext(nextCtx) {
            ctx = nextCtx;
            syncDocks();
            emitChange('context');
            return api;
        },

        get context() { return ctx; },
        get(id) { return layers.get(id) || null; },
        all() { return [...layers.values()]; },
        available() { return availableLayers(); },
        enabled() { return enabledLayers(); },
        isEnabled(id) {
            const layer = layers.get(id);
            return !!layer && isAvailable(layer) && intentFor(layer);
        },

        // Inline layers, for the body renderer to apply after it builds the
        // text. Returned in registration order so the visual stacking of
        // markers is predictable rather than dependent on toggle history.
        inline() {
            return enabledLayers().filter(l => l.placement === 'inline');
        },

        setEnabled(id, on) {
            const layer = layers.get(id);
            if (!layer || !isAvailable(layer)) return api;
            if (intentFor(layer) === !!on) return api;
            intent.set(id, !!on);
            // An explicit toggle means the reader has left the preset behind.
            activeProfile = null;
            persist();
            syncDocks();
            emitChange(layer.placement === 'inline' ? 'inline-toggle' : 'dock-toggle');
            return api;
        },

        toggle(id) { return api.setEnabled(id, !api.isEnabled(id)); },

        // Presets. The public variants site and the private full edition are
        // the same code with different profiles -- 'variant-lab' foregrounds
        // the classification apparatus, 'reading' foregrounds commentary.
        applyProfile(name) {
            if (!profiles[name]) throw new Error(`applyProfile: unknown profile "${name}"`);
            activeProfile = name;
            restored = null;
            // A profile is a fresh statement of intent, so drop what was
            // decided before and let intentFor() re-derive from the profile.
            intent.clear();
            persist();
            syncDocks();
            emitChange('profile');
            return api;
        },

        get profile() { return activeProfile; },
        profileNames() { return Object.keys(profiles); },

        onChange(fn) {
            changeSubscribers.add(fn);
            return () => changeSubscribers.delete(fn);
        },

        dock(name) { return docks[name] || null; },
    };

    return api;
}
